import { pointLetter } from "@/lib/tool-kit/worksheets/numberline";
import {
  autoLabelEvery,
  type PlaneSpec,
  type Point,
  planeGeometry,
  planeRange,
} from "@/lib/tool-kit/worksheets/plane";

export type Connect = "none" | "open" | "closed";

const SIZE = 640;
const PAD = 46;
const ARROW = 20;
const INK = "#171717";
const GRID = "#c4c4c4";
const FONT = "ui-sans-serif, system-ui, sans-serif";
const QUADRANT_ROMAN = ["I", "II", "III", "IV"] as const;

const round = (value: number) => Math.round(value * 100) / 100;
const minus = (value: number) =>
  value < 0 ? `−${Math.abs(value)}` : String(value);

function Arrow({
  x,
  y,
  dir,
}: {
  x: number;
  y: number;
  dir: "right" | "left" | "up" | "down";
}) {
  const wing = 7;
  const length = 14;
  const points = {
    right: `${x},${y} ${x - length},${y - wing} ${x - length},${y + wing}`,
    left: `${x},${y} ${x + length},${y - wing} ${x + length},${y + wing}`,
    up: `${x},${y} ${x - wing},${y + length} ${x + wing},${y + length}`,
    down: `${x},${y} ${x - wing},${y - length} ${x + wing},${y - length}`,
  }[dir];
  return <polygon fill={INK} points={points} />;
}

/** A coordinate plane as SVG: grid, axes, numbers, optional points and connecting line. */
export function PlaneSvg({
  spec,
  points,
  letters,
  connect,
  xName,
  yName,
  numbered,
  labelEvery,
  quadrantLabels,
  label,
}: {
  spec: PlaneSpec;
  points: readonly Point[];
  letters: boolean;
  connect: Connect;
  xName: string;
  yName: string;
  numbered: boolean;
  /** 0 picks a step from the cell size. */
  labelEvery: number;
  quadrantLabels: boolean;
  label: string;
}) {
  const geo = planeGeometry(spec, SIZE, PAD);
  const range = planeRange(spec);
  const four = spec.quadrants === "four";
  const step = labelEvery > 0 ? labelEvery : autoLabelEvery(geo.cell);
  const fontSize = round(Math.max(9, Math.min(16, geo.cell * 0.55)));
  const values = Array.from(
    { length: range.maxX - range.minX + 1 },
    (_, i) => range.minX + i
  );
  const originX = geo.px(0);
  const originY = geo.py(0);
  const path = points.map((p) => `${geo.px(p.x)},${geo.py(p.y)}`).join(" ");
  return (
    <svg
      aria-label={label}
      className="block h-auto w-full"
      role="img"
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      {quadrantLabels && four
        ? QUADRANT_ROMAN.map((numeral, index) => {
            const sx = index === 0 || index === 3 ? 1 : -1;
            const sy = index < 2 ? 1 : -1;
            return (
              <text
                dominantBaseline="central"
                fill="#dedede"
                fontFamily={FONT}
                fontSize={46}
                fontWeight={700}
                key={numeral}
                textAnchor="middle"
                x={geo.px((sx * spec.max) / 2)}
                y={geo.py((sy * spec.max) / 2)}
              >
                {numeral}
              </text>
            );
          })
        : null}
      {values.map((v) => (
        <g key={`grid-${v}`}>
          {v === 0 ? null : (
            <>
              <line
                stroke={GRID}
                strokeWidth={1}
                x1={geo.px(v)}
                x2={geo.px(v)}
                y1={geo.py(range.maxY)}
                y2={geo.py(range.minY)}
              />
              <line
                stroke={GRID}
                strokeWidth={1}
                x1={geo.px(range.minX)}
                x2={geo.px(range.maxX)}
                y1={geo.py(v)}
                y2={geo.py(v)}
              />
            </>
          )}
        </g>
      ))}
      <line
        stroke={INK}
        strokeWidth={2.6}
        x1={geo.px(range.minX) - (four ? ARROW : 0)}
        x2={geo.px(range.maxX) + ARROW}
        y1={originY}
        y2={originY}
      />
      <line
        stroke={INK}
        strokeWidth={2.6}
        x1={originX}
        x2={originX}
        y1={geo.py(range.minY) + (four ? ARROW : 0)}
        y2={geo.py(range.maxY) - ARROW}
      />
      <Arrow dir="right" x={geo.px(range.maxX) + ARROW} y={originY} />
      <Arrow dir="up" x={originX} y={geo.py(range.maxY) - ARROW} />
      {four ? (
        <>
          <Arrow dir="left" x={geo.px(range.minX) - ARROW} y={originY} />
          <Arrow dir="down" x={originX} y={geo.py(range.minY) + ARROW} />
        </>
      ) : null}
      {numbered
        ? values.map((v) => {
            if (v === 0 || v % step !== 0) {
              return null;
            }
            return (
              <g
                fill={INK}
                fontFamily={FONT}
                fontSize={fontSize}
                key={`num-${v}`}
              >
                <text
                  textAnchor="middle"
                  x={geo.px(v)}
                  y={round(originY + fontSize + 7)}
                >
                  {minus(v)}
                </text>
                <text
                  dominantBaseline="central"
                  textAnchor="end"
                  x={originX - 9}
                  y={geo.py(v)}
                >
                  {minus(v)}
                </text>
              </g>
            );
          })
        : null}
      {numbered && !four ? (
        <text
          fill={INK}
          fontFamily={FONT}
          fontSize={fontSize}
          textAnchor="end"
          x={originX - 9}
          y={round(originY + fontSize + 7)}
        >
          0
        </text>
      ) : null}
      <text
        fill={INK}
        fontFamily={FONT}
        fontSize={24}
        fontStyle="italic"
        x={geo.px(range.maxX) + ARROW + 8}
        y={originY + 8}
      >
        {xName}
      </text>
      <text
        fill={INK}
        fontFamily={FONT}
        fontSize={24}
        fontStyle="italic"
        textAnchor="middle"
        x={originX + 16}
        y={geo.py(range.maxY) - ARROW - 8}
      >
        {yName}
      </text>
      {connect === "closed" && points.length > 1 ? (
        <polygon
          fill="none"
          points={path}
          stroke={INK}
          strokeLinejoin="round"
          strokeWidth={2.4}
        />
      ) : null}
      {connect === "open" && points.length > 1 ? (
        <polyline
          fill="none"
          points={path}
          stroke={INK}
          strokeLinejoin="round"
          strokeWidth={2.4}
        />
      ) : null}
      {points.map((p, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: points are positional
        <g key={`point-${index}`}>
          <circle cx={geo.px(p.x)} cy={geo.py(p.y)} fill={INK} r={6.5} />
          {letters ? (
            <text
              fill={INK}
              fontFamily={FONT}
              fontSize={21}
              fontWeight={700}
              paintOrder="stroke"
              stroke="#ffffff"
              strokeWidth={4}
              x={geo.px(p.x) + 9}
              y={geo.py(p.y) - 9}
            >
              {pointLetter(index)}
            </text>
          ) : null}
        </g>
      ))}
    </svg>
  );
}
